import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import { PILLAR_PAGE_MAP, PillarPage } from 'src/constants/searchPages'
import { IPost } from 'src/types/hygraph'
import { formatTimestampMonthDayYear } from 'src/utils'
import { getPostTopic } from 'src/utils/hygraph'
import { RedirectUrls } from '../../types/types'
import SocialMediaIcons from '../Footer/SocialMediaIcons'
import LikedArticle from '../Help/LikedArticle'
import RichTextContainer from '../Help/RichTextContainer'

type IProps = {
  post: IPost
  formattedDate?: string
}

function Blog({ post, formattedDate }: IProps) {
  const { t } = useTranslation('blog')

  const { basePath, topic } = getPostTopic(post)
  const { pillar, label: topicLabel } = topic ?? {}
  const pillarLabel = pillar
    ? pillar === 'anfitrion'
      ? 'Anfitrión'
      : PILLAR_PAGE_MAP[pillar].label
    : 'SpotMe'

  return (
    <article>
      {/* Editorial header rhythm */}
      <header className="border-b border-ink-100/15 pt-[calc(var(--nav-h)+40px)] pb-12 md:pt-[calc(var(--nav-h)+64px)] md:pb-16">
        {/* Breadcrumbs — Fraunces uppercase */}
        <nav
          aria-label="Breadcrumb"
          className="flex flex-wrap items-center gap-x-2.5 gap-y-1 font-display text-[10px] tracking-[0.18em] uppercase md:text-[11px]"
        >
          <Link
            href="/"
            className="text-ink-200 transition-colors hover:text-ink-700"
          >
            {t('ARTICLE.BREADCRUMB_HOME')}
          </Link>
          <span aria-hidden="true" className="text-ink-100/40">
            /
          </span>
          <Link
            href={`/${pillar ?? PillarPage.Parking}`}
            className="text-ink-200 transition-colors hover:text-ink-700"
          >
            {pillarLabel}
          </Link>
          <span aria-hidden="true" className="text-ink-100/40">
            /
          </span>
          <Link
            href={basePath ?? RedirectUrls.Blog}
            className="text-ink-200 transition-colors hover:text-ink-700"
          >
            {t('ARTICLE.BREADCRUMB_BLOG')}
          </Link>
        </nav>

        {/* Eyebrow */}
        <p className="mt-8 font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:mt-10 md:text-xs">
          {topicLabel ?? t('ARTICLE.EYEBROW')}
        </p>

        {/* H1 — Fraunces medium, large */}
        <h1 className="mt-4 max-w-4xl font-display text-[30px] leading-[1.08] font-medium tracking-[-0.015em] text-ink-700 md:mt-5 md:text-[52px] lg:text-[60px]">
          {post?.title}
        </h1>

        {post?.description && (
          <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-ink-200 md:mt-8 md:text-[17px]">
            {post.description}
          </p>
        )}

        {/* Meta strip */}
        <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-3 md:mt-12">
          {post?.author?.picture?.url && (
            <div className="relative size-10 shrink-0 overflow-hidden rounded-full bg-cream-300">
              <Image
                src={post.author.picture.url}
                alt={post.author.name ?? ''}
                fill
                sizes="40px"
                className="object-cover"
              />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] tracking-[0.04em] text-ink-200 md:text-[13px]">
            {post?.author?.name && (
              <>
                <span className="text-ink-200">{t('ARTICLE.BY')}</span>
                <span className="text-ink-700">{post.author.name}</span>
              </>
            )}
            {post?.author?.name && (post?.date ?? post?.createdAt) && (
              <span aria-hidden="true" className="text-ink-100/40">
                ·
              </span>
            )}
            {(post?.date ?? post?.createdAt) && (
              <time dateTime={post.date ?? post.createdAt}>
                {formattedDate ??
                  formatTimestampMonthDayYear(post.date ?? post.createdAt)}
              </time>
            )}
          </div>
        </div>
      </header>

      {/* Cover image — full-bleed inside container, no rounded */}
      {post.coverImage?.url && (
        <figure className="relative mt-10 aspect-[16/9] w-full overflow-hidden bg-cream-300 md:mt-14">
          <Image
            src={post.coverImage.url}
            alt={post.coverImageAlt ?? ''}
            fill
            sizes="(max-width: 900px) 100vw, 70vw"
            quality={75}
            className="object-cover"
            priority
          />
        </figure>
      )}

      {/* Content */}
      <div className="prose prose-lg prose-headings:font-display prose-headings:font-medium prose-headings:tracking-[-0.005em] prose-headings:text-ink-700 prose-h2:mt-14 prose-h2:text-[26px] md:prose-h2:text-[34px] prose-h3:text-[20px] md:prose-h3:text-[24px] prose-p:text-[16px] prose-p:leading-[1.7] prose-p:text-ink-700 md:prose-p:text-[17px] prose-a:font-medium prose-a:text-blue-500 prose-a:no-underline hover:prose-a:underline prose-strong:text-ink-700 prose-blockquote:border-l-2 prose-blockquote:border-blue-500 prose-blockquote:pl-5 prose-blockquote:text-ink-200 prose-blockquote:italic mt-12 max-w-none md:mt-16">
        <RichTextContainer content={post.content} />
      </div>

      {/* Reader feedback */}
      <div className="mt-16 md:mt-24">
        <LikedArticle
          title={post?.title ?? ''}
          slug={post?.slug ?? ''}
          topic={topic?.topic ?? ''}
          surface="blog"
        />
      </div>

      {/* Share strip — hairline editorial */}
      <div className="mt-16 border-t border-ink-100/15 pt-10 md:mt-24 md:pt-12">
        <div className="md:grid md:grid-cols-12 md:gap-12">
          <div className="md:col-span-5">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {t('SOCIAL.EYEBROW')}
            </p>
            <p className="mt-3 font-display text-[18px] leading-snug text-ink-700 md:text-[20px]">
              {t('ARTICLE.SHARE')}
            </p>
          </div>
          <div className="mt-5 md:col-span-7 md:mt-0 md:flex md:items-end md:justify-end">
            <SocialMediaIcons
              primaryColor="text-ink-700"
              justifyContent="start"
            />
          </div>
        </div>
      </div>
    </article>
  )
}

export default Blog
